import { ComponentStatus } from '../components/student-slip-modal/student-slip-modal.store';
import {
  ConsentV1ComponentPayload,
  ConsentV1ComponentResponsePayload,
  ConsentV1ComponentType,
  CostV1ComponentPayload,
  CostV1ComponentType,
  ItemChoicesV1ComponentPayload,
  ItemChoicesV1ComponentResponsePayload,
  ItemChoicesV1ComponentType,
  QuestionItemType,
  QuestionsV1ComponentPayload,
  QuestionsV1ComponentResponsePayload,
  QuestionsV1ComponentType,
  SlipDto,
  SlipStatus
} from '../services/slip.service';
import { Publisher } from './publisher.model';

export interface Consent {
  componentId: number;
  componentStatus: ComponentStatus;
  quickReference: string;
  approvalWording: string;
  isRequired: boolean;
  canDecline: boolean;
  response: {
    granted: boolean;
  };
}
export interface Payment {
  componentId: number;
  componentStatus: ComponentStatus;
  due: number;
  balance: number;
  minimumAmount: number;
}
export interface Questions {
  componentId: number;
  componentStatus: ComponentStatus;
  items: QuestionItemType[];
  // true if questions answered by another individual, answers are not returned
  // in this case
  redacted: boolean;
  answers: any[];
}
export interface ItemChoicesComponent {
  componentId: number;
  componentStatus: ComponentStatus;
  payload: ItemChoicesV1ComponentPayload;
  responsePayload: ItemChoicesV1ComponentResponsePayload;
}
export class Slip {
  id: number;
  supported: boolean;
  category: string;
  title: string;
  description: string;
  slipStatus: SlipStatus;
  slipComponentStatus: ComponentStatus;
  createdOn: string;
  recipientPersonId: number;
  removedOn: string;
  publisher: Publisher;
  balance: {
    due: number;
    amount: number;
  };
  dueOn: {
    date: string;
    overdue: boolean;
  };
  consent: Consent;
  payment: Payment;
  questions: Questions;
  itemChoicesComponent: ItemChoicesComponent;

  static create(dto: SlipDto): Slip {
    const slip = new Slip();
    slip.id = dto.id;
    slip.category = dto.category;
    slip.title = dto.title;
    slip.description = dto.description;
    slip.slipStatus = dto.status;
    slip.createdOn = dto.createdOn;
    slip.recipientPersonId = dto.recipientPersonId;
    slip.removedOn = dto.removedOn;
    slip.publisher = { ...dto.publisher };
    slip.balance = { ...dto.balance };

    slip.supported = Slip.isSupported(dto);
    if (slip.supported) {
      slip.consent = Slip.extractConsent(dto);
      slip.payment = Slip.extractPayment(dto, slip.consent);
      slip.questions = Slip.extractQuestions(dto, slip.consent);
      slip.itemChoicesComponent = Slip.extractItemChoices(dto, slip.consent);
      slip.slipComponentStatus = Slip.determineSlipStatus(
        slip.consent,
        slip.payment,
        slip.questions,
        slip.itemChoicesComponent,
        slip.slipStatus === SlipStatus.Finished
      );

      // We currently only support a single milestone which has an optional due date
      const dueOn = dto.milestones[0]?.dueOn
        ? {
            date: dto.milestones[0].dueOn,
            overdue:
              !['done', 'declined', 'expired'].includes(slip.slipComponentStatus) &&
              Date.now() > Date.parse(dto.milestones[0].dueOn) + 24 * 60 * 60 * 1000
          }
        : null;
      slip.dueOn = dueOn;
    }

    return slip;
  }

  private static extractConsent(slip: SlipDto): Consent {
    const consentDto = slip.milestones[0].components.find(c => c.type === ConsentV1ComponentType);
    if (!consentDto) return null;
    const componentPayload = JSON.parse(consentDto.payload) as ConsentV1ComponentPayload;
    const responseDto = slip.componentResponses.find(cr => cr.componentId === consentDto.id);
    let response = null;
    if (responseDto) {
      const responsePayload = JSON.parse(responseDto.response) as ConsentV1ComponentResponsePayload;
      response = {
        granted: responsePayload.answer === 'granted'
      };
    }

    return {
      componentId: consentDto.id,
      componentStatus: response ? 'done' : slip.status === SlipStatus.Finished ? 'expired' : 'pending',
      approvalWording: componentPayload.approvalWording,
      quickReference: componentPayload.quickReference,
      canDecline: componentPayload.declineOption,
      isRequired: componentPayload.flexibility === 'required',
      response: response
    };
  }

  private static extractPayment(slip: SlipDto, consent: Consent): Payment {
    const costDto = slip.milestones[0].components.find(c => c.type === CostV1ComponentType);
    if (!costDto) return null;
    const costPayload = JSON.parse(costDto.payload) as CostV1ComponentPayload;

    // Note: Don't use costPayload for due amount as it can be adjusted
    return {
      componentId: costDto.id,
      due: slip.balance.due,
      balance: slip.balance.amount,
      componentStatus: this.determinePaymentComponentStatus(
        consent,
        slip.status === SlipStatus.Finished,
        slip.balance.due,
        slip.balance.amount
      ),
      minimumAmount: costPayload.minimumAmount
    };
  }

  private static determinePaymentComponentStatus(
    consent: Consent,
    slipClosed: boolean,
    due: number,
    balance: number
  ): ComponentStatus {
    if (balance <= 0) return 'done';
    if (consent && consent.isRequired && consent.response?.granted === false) return 'void';
    if (consent && !consent.response && !slipClosed) return 'restricted';
    if (slipClosed) {
      return 'expired';
    } else {
      return due !== balance ? 'in-progress' : 'pending';
    }
  }

  private static extractItemChoices(slip: SlipDto, consent: Consent): ItemChoicesComponent {
    const dto = slip.milestones[0].components.find(c => c.type === ItemChoicesV1ComponentType);
    if (!dto) return null;
    const payload = JSON.parse(dto.payload) as ItemChoicesV1ComponentPayload;
    const finalResponseDto = slip.componentResponses.find(cr => cr.componentId === dto.id && cr.status === 'final');

    const responsePayload = finalResponseDto
      ? (JSON.parse(finalResponseDto.response) as ItemChoicesV1ComponentResponsePayload)
      : null;

    return {
      componentId: dto.id,
      payload: payload,

      responsePayload: responsePayload,
      componentStatus: this.determineQuestionsComponentStatus(
        consent,
        !!finalResponseDto,
        slip.status === SlipStatus.Finished
      )
    };
  }

  private static extractQuestions(slip: SlipDto, consent: Consent): Questions {
    const questionsDto = slip.milestones[0].components.find(c => c.type === QuestionsV1ComponentType);
    if (!questionsDto) return null;
    const questionsPayload = JSON.parse(questionsDto.payload) as QuestionsV1ComponentPayload;
    const responseDto = slip.componentResponses.find(cr => cr.componentId === questionsDto.id);
    let answers = null;
    if (responseDto) {
      const responsePayload = JSON.parse(responseDto.response) as QuestionsV1ComponentResponsePayload;
      answers = responsePayload?.answers ?? null;
    }

    return {
      componentId: questionsDto.id,
      items: questionsPayload.questions,
      redacted: !!responseDto && !answers,
      answers: answers,
      componentStatus: this.determineQuestionsComponentStatus(
        consent,
        !!responseDto,
        slip.status === SlipStatus.Finished
      )
    };
  }
  private static determineQuestionsComponentStatus(
    consent: Consent,
    hasResponse: boolean,
    slipClosed: boolean
  ): ComponentStatus {
    if (hasResponse) return 'done';
    if (consent && consent.isRequired && consent.response?.granted === false) return 'void';
    if (consent && !consent.response && !slipClosed) return 'restricted';
    return slipClosed ? 'expired' : 'pending';
  }

  private static determineSlipStatus(
    consent: Consent,
    payment: Payment,
    questions: Questions,
    itemChoices: ItemChoicesComponent,
    slipClosed: boolean
  ): ComponentStatus {
    if (consent && consent.isRequired && consent.response?.granted === false) return 'declined';
    if (
      (!consent || consent?.componentStatus === 'done') &&
      (!questions || questions.componentStatus === 'done') &&
      (!payment || payment.componentStatus === 'done') &&
      (!itemChoices || itemChoices.componentStatus === 'done')
    )
      return 'done';
    if (slipClosed) {
      return 'expired';
    } else {
      if (consent && consent.componentStatus !== 'done') {
        // other components will be restricted
        return 'pending';
      } else {
        return (!consent || consent.componentStatus === 'pending') &&
          (!questions || questions.componentStatus === 'pending') &&
          (!payment || payment.componentStatus === 'pending')
          ? 'pending'
          : 'in-progress';
      }
    }
  }

  private static isSupported(slip: SlipDto): boolean {
    if (slip.milestones.length !== 1) return false;
    if (slip.milestones[0].components.length === 0) return false;
    if (
      slip.milestones[0].components.find(
        c =>
          ![CostV1ComponentType, ConsentV1ComponentType, QuestionsV1ComponentType, ItemChoicesV1ComponentType].includes(
            c.type
          )
      )
    )
      return false;
    return true;
  }
}
